import React, { FC, useCallback } from 'react';

import { isUndefined } from 'lodash';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { ActionButtonVariants } from 'components/action-button';
import { IconLoyaltyTiersLock } from 'components/icons/loyalty-tiers-lock';
import { Modal } from 'components/modal';
import { LoyaltyTierKey } from 'generated/graphql-gateway';
import {
  ButtonContainer,
  ButtonsWrapper,
  ImageWrapper,
  LockOverlay,
  OrderMoreButton,
  Padding,
  RewardDescription,
  RewardDetailsContainer,
  RewardName,
  RewardPictureLock,
  StyledRewardImage,
  TierRequirementTag,
  TierRequirementText,
  Wrapper,
} from 'pages/loyalty/loyalty-incentives-components/incentive-details/incentive-details.styled';
import { RewardPoints } from 'pages/loyalty/loyalty-incentives-components/incentive-details/reward-points';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { RuleType } from 'state/loyalty/types';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';

import { ILoyaltyRewardsUnauthModalProps } from './types';

export const LoyaltyRewardsUnauthModal: FC<ILoyaltyRewardsUnauthModalProps> = ({
  selectedReward,
  onDismiss,
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { getContentForTier } = useFeaturesLoyaltyTiersContent();

  const rewardPoints =
    selectedReward?.ruleSet?.find(rule => rule.ruleSetType === RuleType.LoyaltyPoints)?.points || 0;

  const tierRequirement = selectedReward?.ruleSet?.find(
    rule => rule.ruleSetType === RuleType.TierList
  )?.tierList;

  const handleButtonClick = useCallback(() => {
    navigate(routes.signIn);
  }, [navigate]);

  return (
    <Modal
      onDismiss={onDismiss}
      eventData={{
        modalAppearanceEventMessage: 'Show reward modal',
      }}
      backgroundColor={Styles.color.white}
    >
      <Wrapper>
        <ImageWrapper hasMargin={true}>
          {selectedReward?.image && (
            <StyledRewardImage
              alt={selectedReward?.imageDescription?.locale || ''}
              image={selectedReward?.image?.locale}
              objectFitContain
            />
          )}
          <LockOverlay />
          <RewardPictureLock fill={primitive.$white} />
        </ImageWrapper>

        <RewardDetailsContainer>
          {!isUndefined(rewardPoints) && !tierRequirement && (
            <RewardPoints pointCost={rewardPoints} locked={!rewardPoints} />
          )}
          {tierRequirement && (
            <TierRequirementTag>
              <IconLoyaltyTiersLock />
              <TierRequirementText>
                {
                  getContentForTier(tierRequirement as LoyaltyTierKey)?.loyaltyTiersUIConfigName
                    ?.locale
                }
              </TierRequirementText>
            </TierRequirementTag>
          )}
          <RewardName>{selectedReward.name?.locale}</RewardName>
          {selectedReward.description && (
            <RewardDescription content={selectedReward.description.locale} />
          )}
          <Padding />
        </RewardDetailsContainer>

        <ButtonsWrapper>
          <ButtonContainer>
            <OrderMoreButton onClick={handleButtonClick} variant={ActionButtonVariants.PRIMARY}>
              {formatMessage({ id: 'loyaltyTiersSignIn' })}
            </OrderMoreButton>
          </ButtonContainer>
        </ButtonsWrapper>
      </Wrapper>
    </Modal>
  );
};
